import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import cx from 'classnames';
import { EarconName, useEarcon } from '../../../hooks/useEarcon';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useMounted } from '../../../hooks/useMounted';
import useOnFocusOut from '../../../hooks/useOnFocusOut';
import { useOnOutsideMouseDown } from '../../../hooks/useOnOutsideMouseDown';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderSlotWithComponentFallback } from '../../../slot';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { convertCSSSizeToNumber } from '../../../util/dom';
import { getScrollbarSize } from '../../../util/dom/getScrollbarSize';
import { findNextIndex, findNextPageIndex, findPreviousIndex, findPreviousPageIndex } from '../../../util/findIndex';
import { setRef } from '../../../util/ref-util';
import { useDirectSpeech } from '../../DirectSpeech/DirectSpeechProvider';
import { OptionPopper } from '../../OptionPopper';
import { Popover } from '../../Popover';
import { SelectBoxDesign } from '../../Select/__internal__/';
import { isOptionPopperSeparator } from './utils/typeguards';
import { getFontFamily, getFontFamilyFromStyle } from './utils/utils';
import bundle from '../NovaComponents-Dropdown-gui-icu.properties';
import cn from './DropdownDesign.css.json';
import './DropdownDesign.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useListId = createUseHtmlId('Dropdown-list');
const useValidationMessageId = createUseHtmlId('Dropdown-ValidationMessage');
const useTextLocalizer = createUseTextLocalizer(bundle);
/**
 * @internal
 */
export const DropdownDesign = props => {
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const isRTL = useIsRTL();
  const mounted = useMounted();
  const defaultPlaceholder = localize('placeholder.msg');
  const {
    count: virtualItemCount,
    renderItem: renderVirtualItem,
    itemHeight: virtualItemHeight,
    width: virtualListWidth
  } = props.virtualItems ?? {};
  const defaultVirtualizerRef = useRef();
  const defaultPageSizeRef = useRef();
  const virtualizerRef = props.virtualItems?.virtualizerRef ?? defaultVirtualizerRef;
  const pageSizeRef = props.virtualItems?.pageSizeRef ?? defaultPageSizeRef;
  const focusable = props.focusable ?? true;
  const isSelectBoxAutoWidth = !props.style?.width;
  const isVirtualListAutoWidth = props.virtualItems && !virtualListWidth;
  const [virtualListAutoWidth, setVirtualListAutoWidth] = useState(undefined);
  const [hasHover, setHasHover] = useState(false);
  const [initialVisit, setInitialVisit] = useState(props.required);
  const [hasFocus, setHasFocus] = useState(false);
  const [editing, setEditing] = useState(false);
  const hasUnselectedValue = !isNullish(props.unselectedValue);
  // null = no null item
  // undefined = default null item
  // object = custom null item
  const hasNullItem = hasUnselectedValue && props.unselectedValue?.item !== null;
  const placeholder = props.unselectedValue?.placeholder;
  const _isItemDisabled = props.isItemDisabled;
  const isItemDisabled = useCallback(index => {
    if (props.hasInput) return false; // isItemDisabled not supported in combobox
    if (hasNullItem && index === 0) return false;
    return !!_isItemDisabled?.(hasNullItem ? index - 1 : index);
  }, [props.hasInput, hasNullItem, _isItemDisabled]);
  const onSelected = index => {
    if (index !== selectedIndex && !isItemDisabled?.(index)) {
      props.onSelected?.(!hasNullItem ? index : index > 0 ? index - 1 : undefined);
      setInitialVisit(false);
    }
  };
  const refSelectBox = useRef(null);
  const refTextElement = useRef(null);
  const refContainer = useRef(null);
  const refPopover = useRef(null);
  const refFocusedListItem = useRef(null);
  const validationMessageId = useValidationMessageId();
  const listId = useListId();
  const itemPreId = `${listId}-item`;
  const nullItemId = `${listId}-nullItem`;
  const nullItem = useMemo(() => {
    if (!hasNullItem) {
      return null;
    }
    const text = props.unselectedValue?.item?.text ?? placeholder ?? defaultPlaceholder;
    return {
      ...props.unselectedValue?.item,
      text,
      id: nullItemId
    };
  }, [props.unselectedValue, hasNullItem, placeholder, defaultPlaceholder, nullItemId]);
  const {
    items,
    displayListItemIcon
  } = useMemo(() => {
    // honor existing props.displayListItemIcon, if any.
    let displayListItemIcon = props.displayListItemIcon;
    if (!props.groups && !props.values || props.virtualItems) {
      return {
        items: undefined,
        displayListItemIcon
      };
    }
    const items = [];
    let index = 0;
    if (nullItem) items.push(nullItem);
    if (props.groups) {
      const getGroupId = (group, index) => group.id ?? `${listId}-group-${index}`.replace(/\s+/g, '');
      const getItemId = (groupId, itemIndex) => `${groupId}-item-${itemIndex}`.replace(/\s+/g, '');
      for (const [groupIndex, group] of props.groups.entries()) {
        const groupId = getGroupId(group, groupIndex);
        for (const [itemIndex, value] of (group.items ?? []).entries()) {
          items.push({
            id: getItemId(groupId, itemIndex),
            ...value
          });
          if (displayListItemIcon === undefined && !isOptionPopperSeparator(value) && value.icon !== undefined) {
            displayListItemIcon = true;
          }
        }
      }
    } else if (props.values) {
      let showSeparator = false;
      for (const value of props.values) {
        if (isOptionPopperSeparator(value)) {
          showSeparator = true;
          continue;
        } else {
          const item = value;
          items.push({
            ...value,
            id: itemPreId + index++,
            showSeparator: item.showSeparator ?? showSeparator
          });
          showSeparator = false;
          if (displayListItemIcon === undefined && value.icon !== undefined) {
            displayListItemIcon = true;
          }
        }
      }
    }
    return {
      items,
      displayListItemIcon: !!displayListItemIcon
    };
  }, [props.displayListItemIcon, props.groups, props.values, props.virtualItems, nullItem, itemPreId, listId]);
  // only return values, excluding null item
  const getValue = useCallback(index => renderVirtualItem?.({
    index
  }) ?? items?.[hasNullItem ? index + 1 : index], [hasNullItem, items, renderVirtualItem]);
  // any items, including null item
  const getItem = useCallback(index => {
    if (hasNullItem) return index === 0 ? nullItem : getValue(index - 1);
    return getValue(index);
  }, [getValue, hasNullItem, nullItem]);
  const length = Math.min(virtualItemCount ? virtualItemCount + (hasNullItem ? 1 : 0) : items?.length ?? 0, 295000);
  const defaultIndex = useMemo(() => findNextIndex({
    length,
    start: 0,
    isValidIndex: index => !isItemDisabled?.(index)
  }) ?? -1, [length, isItemDisabled]);
  const getNormalizedIndex = index => {
    if (isNullish(index)) {
      if (props.unselectedValue && !hasNullItem) return -1;
      return defaultIndex;
    }
    const len = virtualItemCount ?? props.values?.length ?? props.groups?.reduce((total, group) => total + (group.items?.length ?? 0), 0) ?? 0;
    index = Math.max(-1, index);
    index = Math.min(index, len - 1);
    return hasNullItem && index !== -1 ? index + 1 : index ?? (props.hasInput ? -1 : 0);
  };
  const selectedIndex = getNormalizedIndex(props.selectedIndex);
  const focusedIndex = getNormalizedIndex(props.focusedIndex);
  const setFocusedIndex = index => props.setFocusedIndex?.(!hasNullItem ? index : index > 0 ? index - 1 : undefined);
  const renderItem = useCallback(({
    index
  }) => {
    if (hasNullItem) {
      if (index <= 0) return nullItem;
      index--;
    }
    const value = getValue(index);
    return {
      ...value,
      id: itemPreId + index
    };
  }, [getValue, hasNullItem, itemPreId, nullItem]);
  const virtualItems = useMemo(() => {
    return props.virtualItems ? {
      count: length,
      renderItem,
      virtualizerRef,
      pageSizeRef,
      width: virtualListWidth ?? virtualListAutoWidth,
      itemHeight: virtualItemHeight
    } : undefined;
  }, [props.virtualItems, length, renderItem, virtualizerRef, pageSizeRef, virtualListWidth, virtualListAutoWidth, virtualItemHeight]);
  const validIndex = !isNullish(focusedIndex) && focusedIndex < length && focusedIndex > -1;
  const focusedItemId = validIndex ? virtualItems ? renderItem({
    index: focusedIndex
  })?.id : getItem(focusedIndex)?.id : undefined;
  const refScrollPosBuffer = useRef(undefined);
  // used to scroll selected item at the top of the viewpoint when popover
  // is opened the first time
  const needsScrollingRef = useRef(false);
  const scrollPopover = useCallback(() => {
    const isFirstItemInGroupWithHeader = index => {
      let count = 0;
      for (const group of props.groups ?? []) {
        if (!group.items || !group.header) continue;
        if (count === index) return true;
        count += group.items.length;
      }
      return false;
    };
    if (virtualItems) {
      virtualizerRef?.current?.scrollToIndex?.(focusedIndex, {
        align: needsScrollingRef.current ? 'start' : 'auto'
      });
    } else {
      /**
       * NOTE: popper does not mount in the DOM until after the isOpen state change
       */
      // we don't need to scroll if the popper is not shown
      if (!refPopover.current || !refFocusedListItem.current) return;
      const offsetDelta = -(refPopover.current.offsetTop + refPopover.current.clientTop);
      const getOffsetTop = element => {
        let offsetTop = 0;
        while (element) {
          offsetTop += element.offsetTop;
          element = element.offsetParent;
        }
        return offsetTop;
      };
      const itemScrollTop = getOffsetTop(refFocusedListItem.current) + offsetDelta;
      const itemScrollBottom = itemScrollTop + refFocusedListItem.current.offsetHeight;
      const scrollTop = refScrollPosBuffer.current ?? refPopover.current.scrollTop;
      if (isFirstItemInGroupWithHeader(focusedIndex)) {
        const headerHeight = refFocusedListItem.current.previousElementSibling?.offsetHeight || 0;
        if (itemScrollTop - headerHeight < scrollTop) {
          refPopover.current.scrollTop = itemScrollTop - headerHeight;
        } else if (itemScrollBottom > scrollTop + refPopover.current.clientHeight) {
          refPopover.current.scrollTop = itemScrollBottom - refPopover.current.clientHeight;
        }
      } else {
        if (itemScrollBottom > scrollTop + refPopover.current.clientHeight) {
          refPopover.current.scrollTop = itemScrollBottom - refPopover.current.clientHeight;
        } else if (itemScrollTop < scrollTop) {
          refPopover.current.scrollTop = itemScrollTop;
        } else if (refScrollPosBuffer.current !== undefined) {
          refPopover.current.scrollTop = refScrollPosBuffer.current;
        }
      }
      refScrollPosBuffer.current = refPopover.current.scrollTop;
    }
  }, [focusedIndex, virtualItems, virtualizerRef, props.groups]);

  //  Make sure dropdown scrolls with the focused item
  useEffect(() => {
    scrollPopover();
  }, [focusedIndex, scrollPopover]);

  // Keep track of whether we still need to do our initial scroll each time popover opens
  useEffect(() => {
    needsScrollingRef.current = props.isOpen;
  }, [props.isOpen]);
  const closePopover = shouldCommitSelection => {
    if (shouldCommitSelection) {
      if (isItemDisabled?.(focusedIndex)) return;
      onSelected(focusedIndex);
    } else {
      setFocusedIndex(selectedIndex);
    }
    props.setIsOpen?.(false);
  };
  const expand = () => {
    props.setIsOpen?.(true);
    // moves the focus to the list
    refSelectBox?.current?.focus();
  };
  const toggleOpen = () => {
    if (props.disabled || props.editable === false) return;
    if (props.isOpen) {
      closePopover(true);
    } else if (props.onBeforeOpen) {
      props.onBeforeOpen().then(preventExpand => {
        if (!preventExpand) {
          expand();
        }
      });
    } else {
      expand();
    }
  };

  // When there is a focus trap, like inside a dialog,
  // that focus is not lost but a drag on the outside
  // may still need to close the popper.
  useOnOutsideMouseDown([refPopover, refSelectBox], () => {
    // since trapping a focus seems delayed so using
    // timeout to wait for a focus, if any
    setTimeout(() => {
      if (document.activeElement === refSelectBox.current) {
        closePopover(true);
        setInitialVisit(false);
      }
    }, 50);
  });
  const getItemsPerPage = () => {
    // Default items per page is set to 10 according to the spec
    const defaultPageSize = 10;
    if (!props.isOpen) return defaultPageSize;
    if (virtualItems) return pageSizeRef.current ?? defaultPageSize;
    const scrollHeight = refPopover.current?.scrollHeight ?? 0;
    const pageHeight = refPopover.current?.offsetHeight ?? 0;
    const itemHeight = scrollHeight / length;
    return Math.round(itemHeight !== 0 && pageHeight !== 0 ? pageHeight / itemHeight : defaultPageSize);
  };
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const earconError = useEarcon({
    source: EarconName.Error
  });
  const handleKeyDown = e => {
    if (length <= 0 || props.disabled || props.readOnly) {
      return;
    }
    let eventHandled = true;
    if (props.hasInput) {
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      setEditing(e.key !== 'Tab');
    }
    switch (e.key) {
      case 'ArrowUp':
        if (!props.isOpen) {
          toggleOpen();
          setFocusedIndex(Math.max(focusedIndex, 0));
        } else {
          const index = findPreviousIndex({
            length,
            start: Math.max(focusedIndex - 1, 0),
            isValidIndex: () => true
          });
          setFocusedIndex(index ?? 0);
          if (index === 0) earcon.play();
        }
        break;
      case 'ArrowDown':
        if (!props.isOpen) {
          toggleOpen();
          setFocusedIndex(Math.max(focusedIndex, 0));
        } else {
          const index = findNextIndex({
            length,
            start: Math.min(focusedIndex + 1, length - 1),
            isValidIndex: () => true
          });
          setFocusedIndex(index ?? length - 1);
          if (index === length - 1) earcon.play();
        }
        break;
      case 'ArrowLeft': /* falls-through */
      case 'ArrowRight':
        if (!props.isOpen || props.hasInput) {
          eventHandled = false;
        }
        break;
      case 'PageUp':
        {
          if (props.isOpen) {
            const index = findPreviousPageIndex({
              length,
              start: Math.max(focusedIndex - 1, 0),
              isValidIndex: () => true,
              pageSize: getItemsPerPage() - 1
            });
            setFocusedIndex(index ?? 0);
            if (index === 0) earcon.play();
          }
        }
        break;
      case 'PageDown':
        {
          if (props.isOpen) {
            const index = findNextPageIndex({
              length,
              start: Math.min(focusedIndex + 1, length - 1),
              isValidIndex: () => true,
              pageSize: getItemsPerPage() - 1
            });
            if (index !== undefined) {
              setFocusedIndex(index);
            }
            if (index === length - 1) earcon.play();
          }
        }
        break;
      case 'Home':
        {
          if (!props.isOpen) {
            toggleOpen();
          }
          const index = findNextIndex({
            length,
            start: 0,
            isValidIndex: () => true
          });
          setFocusedIndex(index ?? 0);
        }
        break;
      case 'End':
        {
          if (!props.isOpen) {
            toggleOpen();
          }
          const index = findPreviousIndex({
            length,
            start: length - 1,
            isValidIndex: () => true
          });
          if (index !== undefined) {
            setFocusedIndex(index);
          }
        }
        break;
      case ' ':
        toggleOpen();
        break;
      case 'Enter':
        if (props.isOpen) {
          closePopover(true);
        } else {
          eventHandled = false;
        }
        break;
      case 'Escape':
        if (props.isOpen) {
          e.preventDefault();
          e.stopPropagation();
          closePopover(false);
        } else {
          eventHandled = false;
        }
        break;
      default:
        if (length <= 0 || e.key.length !== 1 || props.hasInput) {
          eventHandled = false;
          break;
        }
        const key = e.key.toLowerCase();
        const index = findNextIndex({
          length,
          start: focusedIndex + 1,
          isValidIndex: index => {
            const value = getItem(index);
            return !isItemDisabled?.(index) && !!getItemText(value ?? undefined)?.toLowerCase().startsWith(key);
          },
          wrap: true
        });
        if (index !== undefined) {
          if (!props.isOpen) {
            toggleOpen();
          }
          setFocusedIndex(index);
        } else earconError.play();
        break;
    }
    if (eventHandled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const getItemText = useCallback(item => {
    if (isNullish(item)) {
      // possibility of incorrect focused index by the consumer?
      if (focusedIndex === -1) return props.hasInput ? undefined : '';
      return '';
    }

    // consumer may set selectedIndex to clear contents of combobox
    if (selectedIndex === -1 && props.hasInput) return undefined;
    if (props.displayIconOnly) return '\ufeff';
    let label = item.text;
    {
      // HACK: this is specific to VA-SDK dropdowns <https://gitlab.sas.com/dvr/html/va-sdk/issues/157>
      const secondaryText = item.secondaryText;
      if (props.displaySecondaryText && !isNullish(secondaryText)) {
        label += ` (${secondaryText})`;
      }
    }
    return label !== '' ? label : '\ufeff';
  }, [focusedIndex, props.displayIconOnly, props.displaySecondaryText, props.hasInput, selectedIndex]);
  const showPlaceholder = hasUnselectedValue && (hasNullItem && focusedIndex === 0 || focusedIndex === -1);
  const placeholderLabel = hasUnselectedValue ? placeholder ?? defaultPlaceholder : undefined;
  const value = length !== 0 && focusedIndex >= 0 && focusedIndex < length ? getItem(focusedIndex) : undefined;
  const itemDisabled = isItemDisabled?.(focusedIndex);
  const label = showPlaceholder || length === 0 ? placeholderLabel : itemDisabled ? '\xa0' : getItemText(value ?? undefined);
  const icon = !itemDisabled && displayListItemIcon && value ? value.icon : undefined;
  const iconAriaLabel = !itemDisabled && displayListItemIcon && value ? value.iconAriaLabel : undefined;
  const iconTooltip = !itemDisabled && displayListItemIcon && (props.displayIconOnly || !!value) ? value?.iconTooltip : undefined;
  const iconAriaHidden = displayListItemIcon && props.displayIconOnly ? false : !!value?.iconAriaHidden;
  const TOTAL_POPPER_BORDER_WIDTH = 2;
  const LIST_EST_PADDING = 28;
  const DEFAULT_SELECT_BOX_MIN_WIDTH = 96;
  const spaceBaseXL = dt.tokens['space-base-xl'];
  const updateWidth = useCallback(() => {
    if (!isSelectBoxAutoWidth && !isVirtualListAutoWidth || !refTextElement.current) return;
    const container = refContainer.current;
    const textElement = refTextElement.current;
    const containerRect = container.getBoundingClientRect();
    const textRect = textElement.getBoundingClientRect();
    const fontFamily = getFontFamily(textElement);
    const containerWidth = containerRect?.width ?? 0;
    const scrollbarSize = getScrollbarSize();
    const ctx = document.createElement('canvas').getContext('2d');
    const maxLength = Math.min(1000, length);
    if (!ctx) return;
    const getItemMaxWidth = ({
      includeIcon,
      includeIndentation,
      includeText,
      includeSecondary,
      fontFamily
    }) => {
      ctx.font = fontFamily;
      let maxWidth = 0;
      for (let i = 0; i < maxLength; i++) {
        const {
          text,
          secondaryText,
          indented,
          icon
        } = getValue(i) || {};
        let itemWidth = 0;
        if (includeIcon) itemWidth += parseInt(icon?.render({}, undefined).props.width ?? '0', 10);
        if (includeIndentation) {
          const level = typeof indented === 'boolean' ? indented ? 1 : 0 : indented;
          if (level && level > -1) itemWidth += level * convertCSSSizeToNumber(spaceBaseXL);
        }
        if (includeText) itemWidth += ctx.measureText(text ?? '').width;
        if (includeSecondary) {
          const secondaryTextFormatted = secondaryText ? ` (${secondaryText})` : '';
          itemWidth += ctx.measureText(secondaryTextFormatted).width;
        }
        maxWidth = Math.max(maxWidth, itemWidth);
      }
      return maxWidth;
    };

    // *** calculations for select box auto width ***
    if (isSelectBoxAutoWidth && textElement.parentElement) {
      let estimatedMaxWidth = getItemMaxWidth({
        fontFamily,
        includeIcon: true,
        includeIndentation: false,
        includeText: !props.displayIconOnly,
        includeSecondary: !props.displayIconOnly && !!props.displaySecondaryText
      });
      if (placeholderLabel) {
        estimatedMaxWidth = Math.max(estimatedMaxWidth, ctx.measureText(placeholderLabel).width);
      }
      if (estimatedMaxWidth) {
        // rtl in FF (NOVACOMMONS-4917): cut off italic in RTL so
        // may need a little wider
        estimatedMaxWidth += isRTL ? 1 : 0;

        // account for the scrollbar to make sure focus outline can be visible
        estimatedMaxWidth += scrollbarSize;
        textElement.parentElement.style.width = `${Math.ceil(estimatedMaxWidth)}px`;
        if (!!!props.style?.minWidth) {
          const gap = !isRTL ? (textRect.left ?? 0) - (containerRect?.left ?? 0) : (containerRect?.right ?? 0) - (textRect.right ?? 0);

          // empty '' to allow default min-width from style class
          container.style.minWidth = estimatedMaxWidth + gap < DEFAULT_SELECT_BOX_MIN_WIDTH ? 'initial' : '';
        }
      }
    }

    // *** calculations for virtual scrolling list auto width ***
    if (isVirtualListAutoWidth && containerWidth > 0) {
      const customListFontFamily = props.listTokens?.font ? getFontFamilyFromStyle(props.listTokens?.font) : undefined;
      let estimatedListMaxWidth = getItemMaxWidth({
        fontFamily: customListFontFamily ?? fontFamily,
        includeIcon: !!props.displayListItemIcon,
        includeIndentation: true,
        includeText: true,
        includeSecondary: true
      });

      // account for scrollbar (assume always visible) and some padding
      estimatedListMaxWidth += scrollbarSize + LIST_EST_PADDING;
      setVirtualListAutoWidth(Math.max(estimatedListMaxWidth, containerWidth - TOTAL_POPPER_BORDER_WIDTH));
    }
  }, [isSelectBoxAutoWidth, isVirtualListAutoWidth, length, props.listTokens?.font, props.displayIconOnly, props.displaySecondaryText, props.style?.minWidth, props.displayListItemIcon, getValue, spaceBaseXL, placeholderLabel, isRTL]);
  const [setParentRef] = useResizeObserverRef(() => updateWidth());
  useEffect(() => {
    if (isSelectBoxAutoWidth || isVirtualListAutoWidth) {
      const maxWidth = window.getComputedStyle(refContainer.current).maxWidth;
      if (maxWidth.indexOf('%') > -1 && refContainer.current?.parentElement) setParentRef(refContainer.current?.parentElement);
    }
    return () => {
      setParentRef(null);
    };
  }, [isSelectBoxAutoWidth, isVirtualListAutoWidth, setParentRef]);
  useEffect(updateWidth, [updateWidth]);
  const getStatus = () => {
    if (!props.required || !isNullish(props.status)) return props.status;
    if (props.disabled || props.readOnly) return 'none';
    const enforceRequired = !initialVisit && (hasNullItem && focusedIndex === 0 || !hasNullItem && focusedIndex === -1) || props.hasInput && focusedIndex === -1 && props.value === '' && (!hasFocus || hasFocus && !editing);
    return enforceRequired ? 'alert' : 'none';
  };
  const status = getStatus();
  useImperativeHandle(props.selectAPIRef, () => ({
    resetRequiredTrigger: () => setInitialVisit(props.required)
  }));
  const message = isNullish(props.message) && props.required && status === 'alert' ? localize('required.msg') : props.message;
  const handleMouseOver = () => {
    setHasHover(true);
  };
  const directSpeech = useDirectSpeech();
  let onBlur, onFocus;
  {
    const handlers = useOnFocusOut(() => {
      if (!mounted.current) return;
      closePopover(!isItemDisabled?.(focusedIndex));
      setHasFocus(false);
      setInitialVisit(false);
    });
    onBlur = () => {
      if (status && message) directSpeech?.notify(message);
      handlers.onBlur();
      if (props.hasInput) {
        setEditing(false);
      }
    };
    onFocus = () => {
      handlers.onFocus();
      setHasFocus(true);
      if (props.hasInput) {
        setEditing(false);
      }
    };
  }
  const handleMouseLeave = () => setHasHover(false);
  const dropdownMaxWidth = isSelectBoxAutoWidth ? props.style?.maxWidth ?? '25rem' : undefined;
  const iconWidth = useMemo(() => displayListItemIcon ? parseInt(icon?.render({}, undefined).props.width ?? 0) : 0, [displayListItemIcon, icon]);
  const style = {
    ...renderInlineCssVars({
      'max-width': dropdownMaxWidth,
      'icon-width': `${iconWidth}px`,
      'font-size-m': dt.tokens['font-size-body-text-m'],
      ...getStatusTokens(dt, status)
    }, 'dropdown'),
    ...renderInlineCssVars({
      'squish-s': dt.tokens['space-squish-s']
    }, 'dropdown-spacing')
  };
  const ariaActiveDescendant = !isNullish(focusedItemId) && props.isOpen ? focusedItemId : undefined;
  const showStatus = (status === 'warning' || status === 'alert') && !!message;
  const getAriaDescribedBy = () => {
    const ariaDescribedBy = [];
    if (showStatus) {
      ariaDescribedBy.push(validationMessageId);
    }
    if (Array.isArray(props.ariaDescribedBy)) {
      props.ariaDescribedBy.length && ariaDescribedBy.push(...props.ariaDescribedBy);
    } else if (!isNullish(props.ariaDescribedBy)) {
      ariaDescribedBy.push(props.ariaDescribedBy);
    }
    return ariaDescribedBy;
  };
  const {
    setIsOpen
  } = props;
  const onClose = useCallback(() => setIsOpen?.(false), [setIsOpen]);
  const popoverAnimation = {
    closed: {
      opacity: 0
    },
    open: {
      opacity: 1,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.25,
        ease: [0, 0.5, 0.2, 1]
      }
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.container, props.className),
    style: {
      ...style,
      ...props.style
    },
    ref: refContainer,
    onKeyDown: handleKeyDown,
    onMouseEnter: handleMouseOver,
    onMouseLeave: handleMouseLeave,
    onBlur: onBlur,
    onFocus: onFocus,
    onContextMenu: e => e.preventDefault(),
    children: [renderSlotWithComponentFallback(props.renderSelectBox, SelectBoxDesign, {
      innerRef: selectBox => {
        refSelectBox.current = selectBox;
        setRef(props.selectBoxRef, selectBox);
      },
      textRef: refTextElement,
      testId: props.testId,
      id: props.id,
      listId,
      isPlaceholder: showPlaceholder,
      label,
      onPress: length <= 0 || props.disabled || props.readOnly ? undefined : toggleOpen,
      isOpen: props.isOpen,
      disabled: props.disabled,
      hasFocus,
      status,
      required: props.required,
      focusable,
      ariaActiveDescendant,
      ariaLabel: props.ariaLabel,
      ariaLabelledBy: props.ariaLabelledBy,
      ariaDescribedBy: getAriaDescribedBy(),
      icon,
      iconAriaLabel,
      iconTooltip,
      iconAriaHidden,
      ...props.selectTokens,
      selectedIndex,
      focusedIndex,
      setFocusedIndex,
      tooltip: props.tooltip ?? getItem(focusedIndex)?.tooltip ?? '',
      readOnly: props.readOnly,
      futureTheme: props.futureTheme
    }), /*#__PURE__*/_jsx(OptionPopper, {
      listId: listId,
      isOpen: props.isOpen,
      focusedIndex: focusedIndex,
      focusedItemRef: refFocusedListItem,
      showSelectedPseudoFocus: props.showSelectedPseudoFocus,
      referenceElementRef: refSelectBox
      // Option popper should recieve the item list with the separators still included
      ,
      items: items,
      virtualItems: virtualItems,
      groups: props.groups,
      nullItem: nullItem ?? undefined,
      ref: popover => {
        // Need to know when our ref is defined so we can trigger scrolling immediately
        refPopover.current = popover;
        if (popover && needsScrollingRef.current) {
          scrollPopover();
          // This ref callback will be called sometimes while OptionPopper is open, only perform initial scroll once
          needsScrollingRef.current = false;
        }
      },
      testId: props.testId,
      popoverTokens: props.popoverTokens,
      className: props.popoverClassName,
      style: {
        '--focus-light-bg': dt.enableNovaTokens ? `${dt.tokens['color-action-background-focus']} ${dt.tokens['style-action-border-focus']} ${dt.tokens['size-action-border-width-focus']}` : `${dt.reportTokens.stateFocus.onLightBg.bd} ${dt.reportTokens.stateFocus.onLightBg.bdStyle} ${dt.reportTokens.stateFocus.onLightBg.bdWidth}`,
        '--focus-dark-bg': dt.enableNovaTokens ? `${dt.tokens['color-action-background-focus-inverse']} ${dt.tokens['style-action-border-focus']} ${dt.tokens['size-action-border-width-focus']}` : `${dt.reportTokens.stateFocus.onDarkBg.bd} ${dt.reportTokens.stateFocus.onDarkBg.bdStyle} ${dt.reportTokens.stateFocus.onDarkBg.bdWidth}`,
        maxWidth: '25rem',
        ...props.popoverStyles
      },
      listTokens: props.listTokens,
      listItemTokens: props.listItemTokens,
      ariaLabelledBy: props.ariaLabelledBy,
      ariaLabel: props.ariaLabel,
      onHeaderSelected: () => refSelectBox.current?.focus(),
      onSelected: ({
        index
      }) => {
        if (isItemDisabled?.(index)) return;
        onSelected(index);
        props.setIsOpen?.(false);
        refSelectBox.current?.focus();
        // When selecting an item, store the current scrollTop in the buffer
        if (!virtualItems) refScrollPosBuffer.current = refPopover.current?.scrollTop || 0;
      },
      displayListItemIcon: displayListItemIcon,
      onClose: onClose,
      focusLockProps: {
        disabled: true
      },
      isItemDisabled: isItemDisabled,
      disableContextMenu: true,
      animation: {
        variants: popoverAnimation,
        ...props.animation
      }
    }), showStatus ? /*#__PURE__*/_jsx("span", {
      "aria-live": "polite",
      role: "status",
      className: cn.offscreen,
      id: validationMessageId,
      children: message
    }) : null, props.isOpen || !hasFocus && !hasHover || !showStatus ? null : /*#__PURE__*/_jsx(Popover, {
      hideArrow: true,
      referenceElementRef: refContainer,
      className: cn.message,
      style: style,
      placement: "bottom-start",
      focusLockProps: {
        disabled: true
      },
      animation: {
        variants: popoverAnimation,
        ...props.animation
      },
      children: /*#__PURE__*/_jsx("span", {
        children: message
      })
    })]
  });
};
const getStatusTokens = (dt, status) => {
  const messageBorder = {
    'message-bd': dt.themeVariant === 'high-contrast' ? `1px solid ${dt.tokens['color-container-border-4']}` : 'none'
  };
  switch (status) {
    case 'alert':
      return {
        ...messageBorder,
        'message-bg': dt.tokens['color-container-background-0'],
        'message-fg': dt.tokens['color-body-text']
      };
    case 'warning':
      return {
        ...messageBorder,
        'message-bg': dt.tokens['color-status-background-warning'],
        'message-fg': dt.tokens['color-status-text-warning']
      };
    default:
      return {};
  }
};
//# sourceMappingURL=DropdownDesign.js.map